import { IconCompare } from "@wrstudios/icons";
import { camelCase, merge, mergeWith } from "lodash";
import React from "react";
import LiveListing from "../comps/LiveListing";
import LiveSideBySide from "../comps/LiveSideBySide";
import LivePreviewComps from "../previews/LivePreviewComps";

export const createModules = ({
  guid,
  subModules,
  pages = [],
  links = {},
  isWebLead
}) => {
  const pageMap = pages.reduce(
    (byKey, page) => ({ ...byKey, [page.key]: page }),
    {}
  );
  const modules = [
    {
      key: "comps",
      title: "Comps",
      url: `/live/${guid}/comps`,
      path: "/live/:guid/comps",
      isHidden: false,
      canHide: false,
      subModules: []
    }
  ]
    .filter((module) => (isWebLead ? !!pageMap[module.key] : true))
    .map((module) => {
      const page = pageMap[module.key];
      return mergeWith({}, module, page, (a, b) => b ?? a);
    });

  const modifiedLinks = getModifiedLinks(subModules, links);
  const newModules = modules.map((module) => {
    const transformedModule = transformModule(module, guid);
    const camelCaseKey = camelCase(transformedModule.key);
    const moduleFromServer = modifiedLinks[camelCaseKey] || {};
    const { key, ...moduleFromServerWithoutKey } = moduleFromServer;

    const transformedSubModules = subModules.map((subModule) => {
      const transformedSubModule = transformSubModule(subModule, guid);
      // * Notes: We don't camelCase here because `listing/12` becomes `listing12`
      const subModuleFromServer = modifiedLinks[transformedSubModule.key] || {};
      const { key, ...subModuleFromServerWithoutKey } = subModuleFromServer;
      return { ...transformedSubModule, ...subModuleFromServerWithoutKey };
    });

    return {
      ...transformedModule,
      ...moduleFromServerWithoutKey,
      subModules: transformedSubModules,
      isHidden: false // Never hide the main comps module
    };
  });
  return newModules;
};

export function transformModule(data) {
  return {
    key: data.key,
    title: data.title,
    footerTitle: data.footerTitle ?? data.title,
    url: data.url,
    path: data.path,
    isHidden: data.isHidden,
    canHide: data.canHide ?? true,
    el: getElement(data),
    previewEl: getPreviewElement(data),
    iconEl: getIconElement(data),
    showSparkle: !!data.showSparkle,
    subModules: [],
    data
  };
}

export function transformSubModule(data, guid) {
  return {
    key: `listing/${data.recordId}`,
    keyWithListingId: `listing/${data.id}`,
    title: data.address,
    footerTitle: data.address,
    url: `/live/${guid}/comps/listing/${data.id}`,
    path: `/live/:guid/comps/listing/${data.id}`,
    isHidden: data.isHidden || false,
    canHide: data.canHide ?? true,
    el: <LiveListing listingRecordId={data.recordId} />,
    subModules: [],
    data
  };
}

// * Notes: We don't camelCase here because `listing/12` becomes `listing12`
function getModifiedLinks(data, links) {
  const newLinks = data.reduce((byKey, listing) => {
    return {
      ...byKey,
      [`listing/${listing.recordId}`]: {
        isHidden: listing.isHidden
      }
    };
  }, {});
  return merge({}, links, newLinks);
}

function getElement(page) {
  switch (page.key) {
    case "comps":
      return <LiveSideBySide />;
    default:
      throw new Error("Invalid key");
  }
}

function getPreviewElement(page) {
  switch (page.key) {
    case "comps":
      return <LivePreviewComps title={page.title} />;
    default:
      throw new Error("Invalid key");
  }
}

function getIconElement(page) {
  switch (page.key) {
    case "comps":
      return <IconCompare />;
    default:
      throw new Error("Invalid key");
  }
}
